import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";

const fmtCurr = (v) => v == null ? "—" : "$" + v.toLocaleString();
const fmtHrs = (h) => h == null ? "—" : h.toLocaleString() + "h";

// Custom tooltip style for dark mode compatibility
const tooltipStyle = {
  backgroundColor: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
  color: "hsl(var(--foreground))",
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
};

const tooltipLabelStyle = {
  color: "hsl(var(--foreground))",
  fontWeight: 400
};

function Spinner() {
  return (
    <div className="h-[300px] flex items-center justify-center">
      <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
    </div>
  );
}

function EmptyState() {
  return <div className="h-[300px] flex items-center justify-center text-muted-foreground">No data</div>;
}

export default function PerformanceCharts({ loading, chartData, trendData, mode, canCost, onArtistClick }) {
  const isCost = mode === "cost" && canCost;
  const vLabel = isCost ? "Allocated Cost" : "Allocated Hours";
  
  const formatTick = (v) => isCost ? "$" + v.toLocaleString() : v + "h";
  const formatTrendTick = (v) => isCost ? "$" + (v / 1000).toFixed(0) + "k" : v + "h";
  const formatTooltip = (v) => isCost ? fmtCurr(v) : fmtHrs(v);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">{vLabel} by Artist</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <Spinner /> : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal vertical={false} stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={formatTick} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11, fill: "hsl(var(--foreground))" }} />
                <Tooltip 
                  formatter={formatTooltip} 
                  contentStyle={tooltipStyle}
                  labelStyle={tooltipLabelStyle}
                  itemStyle={{ color: "hsl(var(--foreground))" }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} cursor="pointer" onClick={(d) => onArtistClick(d.aid)}>
                  {chartData.map((e, i) => <Cell key={i} fill={e.hr ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">{isCost ? "Cost" : "Hours"} Trend (6 Mo)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <Spinner /> : trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={trendData} margin={{ left: 10, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={formatTrendTick} />
                <Tooltip 
                  formatter={(v) => [formatTooltip(v), isCost ? "Cost" : "Hours"]} 
                  contentStyle={tooltipStyle}
                  labelStyle={tooltipLabelStyle}
                  itemStyle={{ color: "hsl(var(--foreground))" }}
                />
                <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState />}
        </CardContent>
      </Card>
    </div>
  );
}
